import { Controller } from '@hotwired/stimulus'
import parsePhoneNumber from 'libphonenumber-js/max'
import { getAddress } from '../api/common/search_address'

export default class extends Controller {
  static targets = [
    'postcode',
    'postcodeError',
    'prefectures',
    'cities',
    'towns',
    'currentStatusHome',
    'currentStatusFacility',
    'facilityStatusLabel',
    'loading',
    'loadingPage1',
    'loadingPage2',
    'moveout',
    'moveoutTitle',
    'symptomOfDementias',
    'selfPropelled',
    'transferToBed',
    'dietaryRestrictionDetails',
    'careInsuranceBurdenRatio',
    'necessaryMedicalTreatments',
    'medicalCareNighttime',
    'medicalCareNighttimeText',
    'gastrosvenorFrequency',
    'sputumSuctionFrequency',
    'nightlySputumSuction',
    'oxygenSupply',
    'insulinInjectionYourself',
    'insulinInjectionCare',
    'contractWithDialysisClinic',
    'demandPlaceholder',
    'email',
    'suggest',
    'telInput',
    'telError',
    'telNext',
    'beforeSubmit',
    'submitContainer',
    'submit',
  ]

  static values = {
    abCategory: String,
  }

  get className () {
    return this.element.dataset.classname
  }

  get isOfferInquiry () {
    return this.element.dataset.isofferinquiry
  }

  updateTowns (towns) {
    this.townsTarget.innerHTML =
      '<option value="">町名を選択してください</option>' +
      towns.map((town) => (
        `<option value="${town.id}">${town.name}</option>`
      )).join()
  }

  updateCities (cities) {
    this.citiesTarget.innerHTML =
      '<option value="">市区町村を選択してください</option>' +
      cities.map((city) => (
        `<option value="${city.id}">${city.name}</option>`
      )).join()
  }

  changePrefecture () {
    if (this.prefectureId) {
      fetch(`/area/prefectures/${this.prefectureId}/cities.json`)
        .then(response => {
          if (!response.ok) throw new Error(`HTTP Error: ${response.status}`)
          return response.json()
        })
        .then(cities => this.updateCities(cities))
        .catch(error => {
          if (typeof Sentry !== 'undefined') {
            Sentry.captureException(error, { tags: { component: 'inquiry-check-vacancy', operation: 'fetch-cities' } })
          }
          console.error('Failed to fetch cities:', error)
        })
    }
    this.citiesTarget.value = ''
    this.townsTarget.value = ''
  }

  fillAddresses () {
    const param = { post_code: this.postcodeTarget.value }

    getAddress(param).then((address) => {
      if (!address.result.prefecture) {
        this.postcodeErrorTarget.style.display = 'flex'
      } else {
        this.postcodeErrorTarget.style.display = 'none'
        this.prefecturesTarget.value = address.result.prefecture.id
        this.fillCities(address.result.city)
        this.fillTowns(address.result.town)
      }
    })
  }

  async fillCities (selectedCity) {
    try {
      const response = await fetch(`/area/prefectures/${selectedCity.prefecture_id}/cities.json`)
      if (!response.ok) throw new Error(`HTTP Error: ${response.status}`)
      const json = await response.json()
      this.updateCities(json)
      this.citiesTarget.value = selectedCity.id
    } catch (error) {
      if (typeof Sentry !== 'undefined') {
        Sentry.captureException(error, { tags: { component: 'inquiry-check-vacancy', operation: 'fill-cities' } })
      }
      console.error('Failed to fetch cities:', error)
    }
  }

  async fillTowns (selectedTown) {
    try {
      const response = await fetch(`/area/cities/${selectedTown.city_id}/towns`)
      if (!response.ok) throw new Error(`HTTP Error: ${response.status}`)
      const json = await response.json()
      this.updateTowns(json)
      this.townsTarget.value = selectedTown.id
    } catch (error) {
      if (typeof Sentry !== 'undefined') {
        Sentry.captureException(error, { tags: { component: 'inquiry-check-vacancy', operation: 'fill-towns' } })
      }
      console.error('Failed to fetch towns:', error)
    }
  }

  changeCity () {
    if (this.cityId) {
      fetch(`/area/cities/${this.cityId}/towns`)
        .then(response => {
          if (!response.ok) throw new Error(`HTTP Error: ${response.status}`)
          return response.json()
        })
        .then(towns => this.updateTowns(towns))
        .catch(error => {
          if (typeof Sentry !== 'undefined') {
            Sentry.captureException(error, { tags: { component: 'inquiry-check-vacancy', operation: 'fetch-towns' } })
          }
          console.error('Failed to fetch towns:', error)
        })
    }
    this.townsTarget.value = ''
  }

  changeCurrentStatus (e) {
    if ((e.target.value === '4' || e.target.value === 'living_alone' || e.target.value === 'living_with_family' || e.target.value === 'other' || e.target.value === 'unknown')) {
      if (e.target.value === '4') {
        const resetElements = document.getElementsByName('inquiry[occupant_current_status_home_id]')
        for (let i = 0; i < resetElements.length; i++) {
          resetElements[i].checked = false
        }
      }

      this.currentStatusHomeTarget.style.display = 'block'
      const inputs = this.currentStatusHomeTarget.querySelectorAll('input[type="radio"]')
      inputs.forEach(input => {
        input.disabled = false
      })
    } else {
      this.currentStatusHomeTarget.style.display = 'none'
      const inputs = this.currentStatusHomeTarget.querySelectorAll('input[type="radio"]')
      inputs.forEach(input => {
        input.disabled = true
      })
    }

    if ((e.target.value === '' || e.target.value === '2' || e.target.value === '3')) {
      this.currentStatusFacilityTarget.style.display = 'block'
      const inputs = this.currentStatusFacilityTarget.querySelectorAll('input[type="radio"]')
      inputs.forEach(input => {
        input.disabled = false
      })
    } else {
      this.currentStatusFacilityTarget.style.display = 'none'
      const inputs = this.currentStatusFacilityTarget.querySelectorAll('input[type="radio"]')
      inputs.forEach(input => {
        input.disabled = true
      })
    }

    // 現在の居場所で施設系（施設=''／老健='2'／老健以外='3'）を選択中は「施設」ラベルの選択色を維持する。
    // サブ質問「施設は老健でしょうか？」は施設ラジオと同一nameを共有するため、選択でcheckedが外れても見た目を保つ。
    // CSSはViewComponentで名前空間スコープされるため、素のクラスではなく名前空間付き（this.className::）で付与する。
    if (this.hasFacilityStatusLabelTarget) {
      const facilitySelected = (e.target.value === '' || e.target.value === '2' || e.target.value === '3')
      if (facilitySelected) {
        this.facilityStatusLabelTarget.classList.add(`${this.className}::is-facility-selected`)
      } else {
        this.facilityStatusLabelTarget.classList.remove(`${this.className}::is-facility-selected`)
      }
    }

    if ((e.target.value === '1' || e.target.value === '2' || e.target.value === '3' || e.target.value === 'living_alone' || e.target.value === 'living_with_family' || e.target.value === 'other' || e.target.value === 'unknown')) {
      this.moveoutTarget.style.display = 'block'
      this.moveoutTarget.querySelector('select').disabled = false

      if (e.target.value === '1' || e.target.value === '2' || e.target.value === '3') {
        this.moveoutTitleTarget.innerHTML = '退院・退去予定の時期を教えてください'
      } else {
        this.moveoutTitleTarget.innerHTML = '入居の期限は決まっていますか？'
      }
    } else {
      this.moveoutTarget.style.display = 'none'
      this.moveoutTarget.querySelector('select').disabled = true
    }
  }

  changeDementia (e) {
    if (e.target.value === '1') {
      this.symptomOfDementiasTarget.style.display = 'block'
      this.symptomOfDementiasTarget.querySelectorAll('input').forEach(element => (element.disabled = false))
    } else {
      this.symptomOfDementiasTarget.style.display = 'none'
      this.symptomOfDementiasTarget.querySelectorAll('input').forEach(element => (element.disabled = true))
    }
  }

  changeCareLevel (e) {
    if (e.target.value === '-1' || e.target.value === '32' || e.target.value === '0') {
      this.careInsuranceBurdenRatioTarget.querySelectorAll('input').forEach(element => (element.required = false))
    } else {
      this.careInsuranceBurdenRatioTarget.querySelectorAll('input').forEach(element => (element.required = true))
    }
  }

  changeMoveSupport (e) {
    const moveSupportQuestionsDisplayedValues = ['00', '11', '22', '33']
    if (e.target.value === '1' || moveSupportQuestionsDisplayedValues.includes(e.target.value)) {
      this.selfPropelledTarget.style.display = 'block'
    } else {
      this.selfPropelledTarget.style.display = 'none'
    }

    if (moveSupportQuestionsDisplayedValues.includes(e.target.value)) {
      this.transferToBedTarget.style.display = 'block'
    } else {
      this.transferToBedTarget.style.display = 'none'
    }
  }

  changeDietaryRestrictions (e) {
    if (e.target.value === 'yes') {
      this.dietaryRestrictionDetailsTarget.style.display = 'block'
    } else {
      this.dietaryRestrictionDetailsTarget.style.display = 'none'
    }
  }

  changeNecessaryMedicalTreatment () {
    const nighttimeIds = new Set(['1', '2', '5', '8', '-1'])

    const selectedNighttimeNames = this.necessaryMedicalTreatmentsTargets
      .filter(el => el.checked && nighttimeIds.has(el.value))
      .map(el => el.parentElement?.querySelector('span')?.textContent?.trim())
      .filter(Boolean)

    const questionText =
      selectedNighttimeNames.length > 0
        ? `「${selectedNighttimeNames.join('、')}」は夜間（18時以降）の対応も必要ですか？`
        : ''

    this.medicalCareNighttimeTextTargets.forEach(t => { t.textContent = questionText })

    if (selectedNighttimeNames.length > 0) {
      this.medicalCareNighttimeTarget.querySelectorAll('input').forEach(element => (element.required = true))
    } else {
      this.medicalCareNighttimeTarget.querySelectorAll('input').forEach(element => {
        element.required = false
      })
    }
  }

  changeInjectionYourself (e) {
    if (e.target.value === '2') {
      this.insulinInjectionCareTarget.style.display = 'block'
      this.insulinInjectionCareTarget.querySelectorAll('input').forEach(element => (element.disabled = false))
    } else {
      this.insulinInjectionCareTarget.style.display = 'none'
      this.insulinInjectionCareTarget.querySelectorAll('input').forEach(element => (element.disabled = true))
    }
  }

  changeDemand (e) {
    if (this.hasDemandPlaceholderTarget) {
      if (e.target.value.length === 0) {
        e.target.previousElementSibling.style.display = 'block'
      } else {
        e.target.previousElementSibling.style.display = 'none'
      }
    }
  }

  showLoading () {
    setTimeout(() => {
      this.loadingPage1Target.style.display = 'none'
      this.loadingPage2Target.style.display = 'block'

      setTimeout(() => {
        this.loadingTarget.style.display = 'none'
      }, 2000)
    }, 7000)
  }

  isSendSmsInquiry (occupantTimingValue) {
    return [1, 2, 3, 4, 5].includes(parseInt(occupantTimingValue))
  }

  inputEmail (e) {
    if (e.target.value.slice(-1) === '@') {
      this.suggestTarget.style.display = 'block'
    }
  }

  clickSuggest (e) {
    this.emailTarget.value = this.emailTarget.value + e.target.dataset.value
    this.suggestTarget.style.display = 'none'
  }

  closeSuggest () {
    this.suggestTarget.style.display = 'none'
  }

  validateTel () {
    const value = this.telInputTarget.value
    if (value) {
      const phoneNumber = parsePhoneNumber(value, 'JP')
      if (phoneNumber?.isValid()) {
        this.telErrorTarget.style.display = 'none'

        if (this.emailTarget.value?.length > 0) {
          this.telNextTarget.disabled = false
        }

        // TODO SMSが送信できないことを伝える
        // smsが送信できる電話番号かどうかを判定し、結果をhidden_fieldにセットする
        document.getElementById('inquiry_is_sms_sendable_tel').value = this.isSmsSendableTel(value).toString()
      } else {
        this.telNextTarget.disabled = true
        this.telErrorTarget.style.display = 'flex'
      }
    }
  }

  validateTelB () {
    const value = this.telInputTarget.value
    if (value) {
      const phoneNumber = parsePhoneNumber(value, 'JP')
      if (phoneNumber?.isValid()) {
        this.enableSubmit()
        this.telErrorTarget.style.display = 'none'
        document.getElementById('inquiry_is_sms_sendable_tel').value = this.isSmsSendableTel(value).toString()
      } else {
        this.disableSubmit()
        this.telErrorTarget.style.display = 'flex'
      }
    } else {
      this.disableSubmit()
      this.telErrorTarget.style.display = 'flex'
    }
  }

  isSmsSendableTel (rawPhoneNumber) {
    const regex = /^(070|080|090)/
    return regex.test(rawPhoneNumber)
  }

  get prefectureId () {
    return this.prefecturesTarget.value
  }

  get cityId () {
    return this.citiesTarget.value
  }

  get necessaryMedicalTreatmentIds () {
    const ret = []
    this.necessaryMedicalTreatmentsTargets.forEach(element => {
      if (element.checked) {
        ret.push(element.value)
      }
    })
    return ret
  }

  nextSubmit () {
    this.submitContainerTargets.forEach(target => { target.classList.remove(`${this.className}::hidden`) })
  }

  enableSubmit () {
    this.submitTargets.forEach(target => { target.disabled = false })
  }

  disableSubmit () {
    this.submitTargets.forEach(target => { target.disabled = true })
  }
}
